import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Avatar, Button, CircularProgress, Divider, Typography } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import api from "../../services/api";
import { normalizeVcardNumber } from "../../utils/vcard";

const conflictMessage = "Este contato já possui atendimento aberto. Localize-o em Atendimentos para assumir ou transferir.";
const errorMessage = error => {
  if (error?.code === "ECONNABORTED") return "A abertura demorou mais que o esperado. Tente novamente.";
  const message = error?.response?.data?.error;
  if (message === "ERR_OTHER_OPEN_TICKET") return conflictMessage;
  // Render only human-readable server errors as text; legacy JSON/error codes are not ticket DTOs.
  if (typeof message === "string" && message.length <= 400 &&
      !/^(?:ERR_|[{\[])/.test(message.trim())) return message;
  if (error?.response?.status === 409) return conflictMessage;
  return "Não foi possível abrir a conversa. Tente novamente.";
};

const VcardPreview = ({ contact, numbers, queueId, whatsappId }) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ticketsContext = useContext(TicketsContext);
  const name = typeof contact === "string" && contact.trim() ? contact.trim() : "Contato compartilhado";
  const number = normalizeVcardNumber(numbers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(null);
  const version = useRef(0);

  useEffect(() => {
    version.current += 1;
    request.current = null;
    setLoading(false);
    setError("");
    return () => {
      version.current += 1;
      request.current = null;
    };
  }, [contact, numbers, queueId, whatsappId, user?.id, user?.companyId]);

  const handleNewChat = async () => {
    if (!number || request.current) return;
    const token = { version: version.current };
    request.current = token;
    setLoading(true);
    setError("");
    const isCurrent = () => request.current === token && version.current === token.version;
    try {
      const { data } = await api.post("/contacts/shared-contact/open-ticket", {
        name, number, queueId, whatsappId
      }, { timeout: 20000 });
      if (!isCurrent()) return;
      const ticket = data?.ticket;
      if (!ticket?.uuid) {
        setError("Não foi possível abrir a conversa. Tente novamente.");
        return;
      }
      if (ticketsContext?.setTabOpen) ticketsContext.setTabOpen(ticket.status || "open");
      history.push("/tickets/" + encodeURIComponent(ticket.uuid));
    } catch (err) {
      if (isCurrent()) setError(errorMessage(err));
    } finally {
      if (isCurrent()) {
        request.current = null;
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ width: 270, maxWidth: "100%", overflowWrap: "anywhere" }} aria-busy={loading}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 4px 12px" }}>
        <Avatar aria-hidden="true">{name.charAt(0).toUpperCase()}</Avatar>
        <div style={{ minWidth: 0 }}>
          <Typography color="textPrimary" variant="subtitle1">{name}</Typography>
          <Typography color="textSecondary" variant="body2">
            {number || "Telefone não disponível neste cartão"}
          </Typography>
        </div>
      </div>
      <Divider />
      {error && <Typography role="alert" color="error" variant="body2" style={{ padding: "8px 4px" }}>{error}</Typography>}
      <Button
        fullWidth
        color="primary"
        onClick={handleNewChat}
        disabled={!number || loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {loading ? "Abrindo..." : error ? "Tentar novamente" : "Conversar"}
      </Button>
    </div>
  );
};

export default VcardPreview;
